<template>
  <main class="tnm-container tnm-public-page tnm-registration__page">
    <component :is="'style'" v-if="activeCustomCss">{{activeCustomCss}}</component>
    <article class="grid-container tnm-registration__card">
      <template v-if="compEvent">
        <header class="tnm-info tnm-registration__info">
          <div class="tnm-registration__meta">
            <span class="tnm-registration__format">{{compEvent.type === 'online' ? t('event_detail_webinar_label', 'WEBINAR') : t('event_detail_event_label', 'EVENT')}}</span>
            <span v-if="eventDuration" class="tnm-registration__duration">{{eventDurationText}}</span>
            <span v-if="compEvent.type === 'online'" class="tnm-registration__live-format"> · {{t('event_detail_live_format', 'Live')}}</span>
          </div>
          <h1 class="tnm-registration__title">{{compEvent.title}}
            <span v-if="compIsLive" data-testid="event-live-badge" class="tnm-live-badge tnm-registration__live-badge">{{t('event_live_badge_text', 'LIVE')}}</span>
          </h1>
          <div class="date tnm-registration__schedule" v-if="compEvent.comp.startDate">
            <div class="tnm-date tnm-registration__start">
              <div class="tnm-date-title tnm-registration__date-label">{{t('event_detail_date_label', 'Datum')}}</div>
              <div class="tnm-date-date tnm-registration__date">{{compEvent.comp.startDate}}</div>
              <div class="tnm-registration__time-label">{{t('event_detail_time_label', 'Zeit')}}</div>
              <div class="tnm-date-time tnm-registration__time">{{compEvent.comp.startTime}}<span v-if="compEvent.comp.endDate === compEvent.comp.startDate"> - {{compEvent.comp.endTime}}</span> {{t('event_detail_time_suffix', 'Uhr')}}</div>
            </div>
            <div class="tnm-date tnm-registration__end" v-if="compEvent.comp.endDate && compEvent.comp.endDate !== compEvent.comp.startDate">
              <div class="tnm-date-title tnm-registration__date-label">{{t('event_detail_end_label', 'Ende:')}}</div>
              <div class="tnm-date-date tnm-registration__date">{{compEvent.comp.endDate}}</div>
              <div class="tnm-date-time tnm-registration__time">{{compEvent.comp.endTime}} {{t('event_detail_time_suffix', 'Uhr')}}</div>
            </div>
          </div>
          <div class="tnm-location tnm-registration__location" v-if="compShowLocationOrWebsite">
            <div v-if="compEvent.type !== 'online'" class="tnm-registration__address">
              <div class="tnm-location-title tnm-registration__location-label">{{t('event_detail_location_label', 'Ort:')}}</div>
              <div class="tnm-location-text tnm-registration__location-text">{{compEvent.street}}<br>{{compEvent.zip}} {{compEvent.city}}</div>
            </div>
            <div v-if="compEvent.website" class="tnm-registration__website">
              <div class="tnm-location-title tnm-registration__location-label">{{t('event_detail_more_info_label', 'Weitere Informationen:')}}</div>
              <a class="tnm-location-text tnm-registration__website-link" :href="compEvent.website" target="_blank" rel="noopener noreferrer">{{compEvent.website}}</a>
            </div>
          </div>
          <div class="tnm-description tnm-registration__description" v-if="compEvent.description" v-html="compEvent.description"></div>
        </header>

        <section v-if="ui.registerError" class="register-form tnm-registration__message tnm-registration__message--error" role="alert">
          <h2 class="tnm-registration__message-title">{{t('registration_error_title', 'Es ist ein Fehler aufgetreten!')}}</h2>
          <p class="tnm-registration__message-text">{{t('registration_error_intro', 'Entschuldigung.')}} {{t('registration_error_help', 'Falls die Fehlermeldung nicht eindeutig ist, wenden Sie sich bitte an den Webmaster.')}}</p>
          <p class="tnm-registration__error">{{local.registerErrorMsg}}</p>
        </section>
        <section v-if="ui.registerSuccess" data-testid="registration-success" class="register-form custom-colors tnm-registration__message tnm-registration__message--success" role="status">
          <h2 class="tnm-registration__message-title">{{t('registration_success_title', 'Fast geschafft!')}}</h2>
          <p class="custom-text-pre-line tnm-registration__message-text">{{t('registration_success_text', 'Ihre Registrierung war erfolgreich.\nBitte bestätigen Sie Ihre Anmeldung mit einem Klick auf den Link, den wir Ihnen soeben per Mail geschickt haben.')}}</p>
        </section>
        <form v-else class="register-form relative tnm-registration__form" @submit.prevent="sendForm()">
          <inner-loader v-if="ui.sendData" />
          <h2 class="register-form-title tnm-registration__form-title">{{t('registration_form_title', 'Bitte geben Sie hier Ihre Daten ein.')}}</h2>
          <p v-if="!compRegistrationOpen" data-testid="registration-closed" class="tnm-registration-closed tnm-registration__closed">{{t('registration_closed_text', 'Eine Anmeldung für dieses Event ist nicht mehr möglich.')}}</p>
          <p class="tnm-registration__required-hint"><span class="tnm-registration__required">*</span> {{t('registration_required_hint', 'zeigt erforderliche Felder an.')}}</p>
          <div class="tnm-form tnm-registration__fields" v-if="compFields">
            <template v-for="(field, fieldKey) of compFieldsGenerated">
              <div v-if="field.isVisible" :key="field.id" class="tnm-registration__field" :class="'tnm-registration__field--' + fieldKey">
                <template v-if="field.type === 'text' || field.type === 'select'">
                  <label class="tnm-registration__label" :for="'registration-' + fieldKey">{{fieldLabel(field)}}<span v-if="field.required" class="tnm-registration__required">*</span></label>
                  <input v-if="field.type === 'text'" class="tnm-registration__input" :id="'registration-' + fieldKey" type="text" :placeholder="fieldLabel(field)" v-model="field.value" :aria-required="field.required" :aria-invalid="!!(field.touched && field.err)" :aria-describedby="field.touched && field.err ? 'registration-error-' + fieldKey : null" @blur="field.touched = true">
                  <select v-else class="tnm-registration__select" :id="'registration-' + fieldKey" v-model="field.value" :aria-required="field.required" :aria-invalid="!!(field.touched && field.err)" :aria-describedby="field.touched && field.err ? 'registration-error-' + fieldKey : null" @blur="field.touched = true">
                    <option v-for="value of field.selectValues" :value="value.value" :key="value.value">{{selectOptionLabel(value)}}</option>
                  </select>
                </template>
                <label v-else-if="field.type === 'boolean'" class="tnm-registration__checkbox-label" :for="'switch' + field.id">
                  <input class="tnm-registration__checkbox" :id="'switch' + field.id" v-model="field.value" type="checkbox" :name="'switch' + field.id" @blur="field.touched = true">
                  <span class="tnm-registration__checkbox-text">{{fieldLabel(field)}}<span v-if="field.required" class="tnm-registration__required">*</span></span>
                </label>
                <p class="help-text warn tnm-registration__field-error" :id="'registration-error-' + fieldKey" v-if="field.touched && field.err">{{formatError(field.err)}}</p>
              </div>
            </template>
            <div v-for="field of compCustomFields" :key="field.id" class="tnm-registration__field" :class="'tnm-registration__field--custom-' + field.id">
              <template v-if="field.type !== 'Boolean'">
                <label class="tnm-registration__label" :for="'registration-custom-' + field.id">{{field.label}}<span v-if="field.required" class="tnm-registration__required">*</span></label>
                <input v-if="field.type !== 'Select'" class="tnm-registration__input" :id="'registration-custom-' + field.id" :type="field.comp.type" :placeholder="field.label" v-model="field.value" :aria-required="field.required" :aria-invalid="!!(field.touched && field.err)" :aria-describedby="field.touched && field.err ? 'registration-custom-error-' + field.id : null" @blur="field.touched = true">
                <select v-else class="tnm-registration__select" :id="'registration-custom-' + field.id" v-model="field.value" :aria-required="field.required" @blur="field.touched = true">
                  <option v-for="value of field.values" :key="value" :value="value">{{value}}</option>
                </select>
              </template>
              <label v-else class="tnm-registration__checkbox-label" :for="'switch' + field.name">
                <input class="tnm-registration__checkbox" :id="'switch' + field.name" v-model="field.value" type="checkbox" :name="'switch' + field.name" @blur="field.touched = true">
                <span class="tnm-registration__checkbox-text">{{field.label}}<span v-if="field.required" class="tnm-registration__required">*</span></span>
              </label>
              <p class="help-text warn tnm-registration__field-error" :id="'registration-custom-error-' + field.id" v-if="field.touched && field.err">{{formatError(field.err)}}</p>
            </div>
            <div v-if="form.allowVideoRecording.isVisible" class="tnm-registration__field tnm-registration__field--allowVideoRecording">
              <label class="tnm-registration__checkbox-label" for="switchVideoRecording">
                <input class="tnm-registration__checkbox" id="switchVideoRecording" v-model="form.allowVideoRecording.value" type="checkbox" name="switchVideoRecording" @blur="compFields.allowVideoRecording.touched = true">
                <span class="tnm-registration__checkbox-text">{{t('registration_video_recording_label', 'Ich akzeptiere, dass Fotos und Videomitschnitte während der Veranstaltung erstellt werden.')}}</span>
              </label>
              <p class="help-text warn tnm-registration__field-error" v-if="compFields.allowVideoRecording.touched && compFields.allowVideoRecording.err">{{formatError(compFields.allowVideoRecording.err)}}</p>
            </div>
            <div v-if="form.allowMarketing.isVisible" class="tnm-registration__field tnm-registration__field--allowMarketing">
              <label class="tnm-registration__checkbox-label" for="switchMarketing">
                <input class="tnm-registration__checkbox" id="switchMarketing" v-model="form.allowMarketing.value" type="checkbox" name="switchMarketing" @blur="compFields.allowMarketing.touched = true">
                <span class="tnm-registration__checkbox-text">{{marketingLabel()}}</span>
              </label>
              <p class="help-text warn tnm-registration__field-error" v-if="compFields.allowMarketing.touched && compFields.allowMarketing.err">{{formatError(compFields.allowMarketing.err)}}</p>
            </div>
            <div class="tnm-registration__field tnm-registration__field--acceptPrivacy">
              <label class="tnm-registration__checkbox-label" for="switchPrivacy">
                <input class="tnm-registration__checkbox" id="switchPrivacy" v-model="form.acceptPrivacy.value" type="checkbox" name="switchPrivacy" aria-required="true" @blur="compFields.acceptPrivacy.touched = true">
                <span class="tnm-registration__checkbox-text">{{t('registration_privacy_label_prefix', 'Ich stimme der')}} <a class="tnm-registration__privacy-link" :href="local.event.dataPrivacyUrl" target="_blank" rel="noopener noreferrer">{{t('registration_privacy_link_text', 'Datenschutzrichtlinie')}}</a> {{t('registration_privacy_label_suffix', 'zu.')}}</span>
              </label>
              <p class="help-text warn tnm-registration__field-error" v-if="compFields.acceptPrivacy.touched && compFields.acceptPrivacy.err">{{formatError(compFields.acceptPrivacy.err)}}</p>
            </div>
            <div class="tnm-registration__actions">
              <button type="submit" data-testid="registration-submit" :disabled="compValidation || !compRegistrationOpen || ui.sendData" class="button tnm-registration__submit" data-text-id="registration_submit_button_text">{{t('registration_submit_button_text', 'Absenden')}}</button>
            </div>
          </div>
        </form>
      </template>
      <p v-else-if="ui.registerError" class="tnm-registration__error" role="alert">{{local.registerErrorMsg}}</p>
      <div v-else class="relative tnm-registration__loading"><inner-loader /></div>
    </article>
  </main>
</template>
<script>
import isEmail from 'validator/lib/isEmail'
import isDate from 'validator/lib/isDate'
import isInt from 'validator/lib/isInt'
import innerLoader from '@/components/innerLoader'
import { setDocumentFavicon } from '../lib/favicon'
import { getCustomText } from '../lib/custom-texts'
import { getEventAvailability } from '../lib/event-availability'
import '../styles/public-events.css'

let height
const sendPostMessage = () => {
  if (height !== document.getElementById('app').offsetHeight) {
    height = document.getElementById('app').offsetHeight
    window.parent.postMessage({
      frameHeight: height
    }, '*');
    console.log(height) // check the message is being sent correctly
  }
}

const sendRegistrationSuccessMessage = (eventId) => {
  if (window.parent && window.parent.postMessage) {
    window.parent.postMessage({
      type: 'event-registration-success',
      eventId
    }, '*')
  }
}

export default {
  name: 'Event',
  components: {innerLoader},
  data() { return {
    state: this.$store.state,
    activeCustomCss: '',
    currentTime: Date.now(),
    availabilityTimer: null,
    local: {
      eventId: null,
      event: null,
      previewKey: null,
      customFields: [],
      //if no server value exists or is invalid
      visibleFieldsFallback: ["email","name","forename"],
      requiredFieldsFallback: ["email"],
      registerErrorMsg: '',
      customCss: '',
      customTexts: {}
    },
    ui: {
      fetchData: false,
      sendData: false,
      registerSuccess: false,
      registerError: false
    },
    /*
    own form validation logic.
    somehow complex, but okay.
    */
    form: {
      title: {
        id: 1,
        type: 'text',
        label: 'Titel',
        textId: 'registration_field_title_label',
        value: '',
        required: false,
        isVisible: true,
        err: false,
        touched: false,
        validate() {
          if (this.required) {
            if (!this.value) {
              this.err = "Dies ist ein Pflichtfeld"
            } else {
              this.err = null
            }
          }
        }
      },
      forename: {
        id: 2,
        type: 'text',
        label: 'Vorname',
        textId: 'registration_field_forename_label',
        value: '',
        required: false,
        isVisible: true,
        err: false,
        touched: false,
        validate() {
          if (this.required) {
            if (!this.value) {
              this.err = "Dies ist ein Pflichtfeld"
            } else {
              this.err = null
            }
          }
        }
      },
      name: {
        id: 3,
        type: 'text',
        label: 'Nachname',
        textId: 'registration_field_name_label',
        value: '',
        required: false,
        isVisible: true,
        err: false,
        touched: false,
        validate() {
          if (this.required) {
            if (!this.value) {
              this.err = "Dies ist ein Pflichtfeld"
            } else {
              this.err = null
            }
          }
        }
      },
      email: {
        id: 4,
        type: 'text',
        label: 'E-Mail',
        textId: 'registration_field_email_label',
        value: '',
        required: true,
        isVisible: true,
        err: false,
        touched: false,
        validate() {
          if (this.required) {
            if (!this.value) {
              this.err = "Dies ist ein Pflichtfeld"
            } else if (!isEmail(this.value)) {
              this.err = "Bitte überprüfen Sie Ihre Eingabe"
            } else {
              this.err = null
            }
          }
        }
      },
      tel: {
        id: 5,
        type: 'text',
        label: 'Telefon',
        textId: 'registration_field_tel_label',
        value: '',
        required: false,
        isVisible: true,
        err: false,
        touched: false,
        validate() {
          if (this.required) {
            if (!this.value) {
              this.err = "Dies ist ein Pflichtfeld"
            } else {
              this.err = null
            }
          }
        }
      },
      city: {
        id: 6,
        type: 'text',
        label: 'Ort',
        textId: 'registration_field_city_label',
        value: '',
        required: false,
        isVisible: true,
        err: false,
        touched: false,
        validate() {
          if (this.required) {
            if (!this.value) {
              this.err = "Dies ist ein Pflichtfeld"
            } else {
              this.err = null
            }
          }
        }
      },
      street: {
        id: 7,
        type: 'text',
        label: 'Strasse',
        textId: 'registration_field_street_label',
        value: '',
        required: false,
        isVisible: true,
        err: false,
        touched: false,
        validate() {
          if (this.required) {
            if (!this.value) {
              this.err = "Dies ist ein Pflichtfeld"
            } else {
              this.err = null
            }
          }
        }
      },
      zip: {
        label: 'PLZ',
        textId: 'registration_field_zip_label',
        id: 8,
        type: 'text',
        value: '',
        required: false,
        isVisible: true,
        err: false,
        touched: false,
        validate() {
          if (this.required) {
            if (!this.value) {
              this.err = "Dies ist ein Pflichtfeld"
            } else {
              this.err = null
            }
          }
        }
      },
      allowMarketing: {
        id: 9,
        excludeFromAuto: true,
        type: 'boolean',
        label: 'erlaube Kontaktaufnahme zu Marketingzwecken',
        textId: 'registration_marketing_label',
        order:1800,
        value: false,
        required: false,
        isVisible: true,
        err: false,
        touched: false,
        validate() {
          if (this.required) {
            if (!this.value) {
              this.err = "Dies ist ein Pflichtfeld"
            } else {
              this.err = null
            }
          }
        }
      },
      gender: {
        id: 10,
        type: 'select',
        label: 'Anrede',
        textId: 'registration_field_gender_label',
        selectValues: [
          { value: 1, name: 'männlich', textId: 'registration_gender_male'},
          { value: 2, name: 'weiblich', textId: 'registration_gender_female'},
          { value: 3, name: 'divers', textId: 'registration_gender_diverse'}
        ],
        value: null,
        required: false,
        isVisible: true,
        err: false,
        touched: false,
        validate() {
          if (this.required) {
            if (!this.value) {
              this.err = "Dies ist ein Pflichtfeld"
            } else {
              this.err = null
            }
          }
        }
      },
      acceptPrivacy: {
        id: 11,
        excludeFromAuto: true,
        type: 'boolean',
        label: 'akzeptiere Datenschutzbestimmungen',
        textId: 'registration_privacy_link_text',
        order: 2000,
        value: false,
        required: true,
        isVisible: true,
        err: false,
        touched: false,
        validate() {
          if (this.required) {
            if (!this.value) {
              this.err = "Dies ist ein Pflichtfeld"
            } else {
              this.err = null
            }
          }
        }
      },
      allowVideoRecording: {
        id: 12,
        excludeFromAuto: true,
        type: 'boolean',
        label: 'Ich akzeptiere, dass Fotos und Videomitschnitte während der Veranstaltung erstellt werden',
        textId: 'registration_video_recording_label',
        order:1600,
        value: false,
        required: true,
        isVisible: true,
        err: false,
        touched: false,
        validate() {
          if (this.required) {
            if (!this.value) {
              this.err = "Dies ist ein Pflichtfeld"
            } else {
              this.err = null
            }
          }
        }
      },
      company: {
        id: 13,
        type: 'text',
        label: 'Unternehmen',
        textId: 'registration_field_company_label',
        value: '',
        required: false,
        isVisible: true,
        err: false,
        touched: false,
        validate() {
          if (this.required) {
            if (!this.value) {
              this.err = "Dies ist ein Pflichtfeld"
            } else {
              this.err = null
            }
          }
        }
      },
    }
  }},
  async mounted() {
    this.local.eventId = this.$route.params.id
    if (this.$route.query.previewKey) {
      this.local.previewKey = this.$route.query.previewKey
      //this.$router.push(this.$route.path)
    }
    try {
      await this.getData()
      this.applyCustomCssFromEvents()
    } catch (error) {
      this.ui.registerError = true
      this.local.registerErrorMsg = this.t('registration_load_error', 'Das Event konnte nicht geladen werden. Bitte versuchen Sie es später erneut.')
      return
    }
    this.availabilityTimer = setInterval(() => {
      this.currentTime = Date.now()
      this.$nextTick(() => {
        sendPostMessage()
      })
    }, 30000)
    setTimeout(() => {
      sendPostMessage()
    },300)
  },
  computed: {
    eventDurationText() {
      return this.t('event_detail_duration_text', '{{minutes}} Minuten', {minutes: this.eventDuration})
    },
    eventDuration() {
      if (!this.local.event || !this.local.event.startDate || !this.local.event.endDate) return null
      const duration = Math.round((new Date(this.local.event.endDate) - new Date(this.local.event.startDate)) / 60000)
      return Number.isFinite(duration) && duration > 0 ? duration : null
    },
    compAvailability() {
      if (!this.local.event) {
        return {
          isLive: false,
          registrationOpen: false
        }
      }

      return getEventAvailability(this.local.event, this.currentTime)
    },
    compRegistrationOpen() {
      if (!this.local.event) return false

      const serverStatus = this.local.event.publicStatus
      const serverAllowsRegistration = !serverStatus ||
        typeof serverStatus.registrationOpen !== 'boolean' ||
        serverStatus.registrationOpen

      return serverAllowsRegistration && this.compAvailability.registrationOpen
    },
    compIsLive() {
      if (!this.local.event) return false
      return this.compAvailability.isLive
    },
    /*
    button state and err message generation for fields
    not the best way in terms of perfomance to put it here, but good enough
    */
    compValidation() {
      if (!this.compRegistrationOpen) return true

      let res = false
      for (const [key, value] of Object.entries(this.compFields)) {
        value.validate()
        if (value.err) res = true
      }
      for (const field of this.compCustomFields) {
        if (field.required && field.type !== 'Boolean') {
          if (!field.value) {
            field.err = "Dies ist ein Pflichtfeld"
          } else if (field.type == 'Number' && !isInt(field.value)) {
            field.err = "Bitte eine Zahl eingeben"
          } else if (field.type == 'Date' && !isDate(field.value)) {
            field.err = "Bitte eine Zahl eingeben"
          }
          else {
            field.err = ""
          }
        }
        if (field.err) res = true
      }
      if (!this.compFields.acceptPrivacy.value) res = true
      return res
    },
    compFields() {
      if (!this.local.event) return null
      let formArray = Object.entries(this.form)
      if (typeof this.local.event.visibleFields === 'string') {
        try {
          this.local.event.visibleFields = JSON.parse(this.local.event.visibleFields)
        } catch(err) {
          console.warn(err)
          this.local.event.visibleFields = this.local.visibleFieldsFallback
        }
        try {
          this.local.event.requiredFields = JSON.parse(this.local.event.requiredFields)
        } catch(err) {
          console.warn(err)
          this.local.event.requiredFields = this.local.requiredFieldsFallback
        }
      }
      formArray = formArray.map(([key, value]) => {
        if (this.local.event.visibleFields?.includes(key)) {
          value.isVisible = true
          value.order = this.local.event.visibleFields.indexOf(key)
        } else {
          value.isVisible = false
          value.order = 1000
        }
        if (this.local.event.requiredFields?.includes(key)) {
          value.required = true
        } else value.required = false
        return [key, value]
      })
      formArray.sort((a, b) => parseInt(a[1].order) - parseInt(b[1].order));
      //console.log(formArray)
      return Object.fromEntries(formArray)
    },
    compFieldsGenerated() {
      if (!this.compFields) return null
      return Object.fromEntries(Object.entries(this.compFields).filter((item) => item[1].excludeFromAuto != true))
    },
    compCustomFields() {
      let fields = this.local.customFields.filter((field) => field.visible)
      fields = fields.map((field) => {
        field.comp = {}
        field.comp.type = null
        if (field.type === 'String') {
          field.comp.type = 'text'
        } else if (field.type === 'Number') {
          field.comp.type = 'number'
        } else if (field.type === 'Date') {
          field.comp.type = 'date'
        } else if (field.type === 'Select') {
          field.comp.type = 'select'
        } else if (field.type === 'Boolean' && field.value === undefined) {
          field.value = false
        }
        return field
      })
      fields.sort((a, b) => a.order - b.order)
      return fields
    },
    compEvent() {
      if (!this.local.event) return null
      let event = this.local.event
      event.comp = {}
      const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: 'Europe/Berlin' }
      if (event.startDate) {
        let startDate = new Date(event.startDate)
        event.comp.startTime = startDate.toLocaleTimeString('de-DE', {hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin'})
        event.comp.startDate = startDate.toLocaleString('de-DE', options)
      }
      if (event.endDate) {
        let endDate = new Date(event.endDate)
        event.comp.endDate = endDate.toLocaleString('de-DE', options)
        event.comp.endTime = endDate.toLocaleTimeString('de-DE', {hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin'})
      }
      return event
    },
    compShowLocationOrWebsite() {
      if (this.local.event.website) return true
      if (this.local.event.type !== 'online') {
        if (this.local.event.street) return true
        if (this.local.event.zip) return true
        if (this.local.event.city) return true
      }
      return false
    }
  },
  methods: {
    async getData() {
      let params = {}
      if (this.local.previewKey) {
        params = {
          previewKey: this.local.previewKey
        }
      }
      params.getPrivacyUrl = true
      let data
      try {
        data = await this.$http.get(this.state.app.apiUrl + '/event/' + this.local.eventId, {params})
      } catch(err) {
        throw err
      }
      this.local.event = data.data
      this.local.customTexts = this.local.event.customTexts || {}
      setDocumentFavicon(this.local.event.faviconUrl)
      console.log("event", this.local.event)
      let customFieldData
      try {
        customFieldData = await this.$http.get(this.state.app.apiUrl + '/custom-field', {params: {
          eventId: this.local.eventId
        }})
      } catch(err) {
        throw err
      }
      this.local.customFields = customFieldData.data.data
      console.log(this.local.customFields)
    },
    applyCustomCssFromEvents() {
      if (this.local.event.customCss) {
        this.activeCustomCss = this.local.event.customCss;
        console.log('Applied custom CSS:', this.activeCustomCss);
      } else {
        this.activeCustomCss = ''; // Kein CSS oder keine Events
      }
    },
    t(id, fallback, variables = {}) {
      return getCustomText(this.local.customTexts, id, fallback, variables)
    },
    fieldLabel(field) {
      return this.t(field.textId, field.label, { company: this.local.event?.company })
    },
    selectOptionLabel(option) {
      return this.t(option.textId, option.name)
    },
    marketingLabel() {
      return this.t(
        'registration_marketing_label',
        'Ich bin einverstanden, dass {{company}} meine Daten für eine weitere Kontaktaufnahme elektronisch verarbeitet. Die Daten werden nicht an Dritte weitergegeben.',
        { company: this.local.event.company }
      )
    },
    formatError(error) {
      if (error === 'Dies ist ein Pflichtfeld') {
        return this.t('registration_required_error', 'Dies ist ein Pflichtfeld')
      }

      if (error === 'Bitte überprüfen Sie Ihre Eingabe') {
        return this.t('registration_invalid_email_error', 'Bitte überprüfen Sie Ihre Eingabe')
      }

      if (error === 'Bitte eine Zahl eingeben') {
        return this.t('registration_invalid_number_error', 'Bitte eine Zahl eingeben')
      }

      return error
    },
    async sendForm() {
      if (!this.compRegistrationOpen || this.compValidation || this.ui.sendData) {
        return
      }

      this.ui.registerError = false
      this.ui.sendData = true
      await this.$sleep(1000)
      let sendObj = {
        eventId: this.local.event.id
      }
      let formArray = Object.entries(this.compFields)
      formArray = formArray.filter(([key, field]) => field.isVisible)
      for (const [key, field] of formArray) {
        sendObj[key] = field.value
      }
      sendObj.customFields = {}
      for (const field of this.compCustomFields) {
        sendObj.customFields[field.name] = field.value
      }
      console.log("sendobj", sendObj)
      let sendResult
      try {
        sendResult = await this.$http.post(this.state.app.apiUrl + '/participant', sendObj)
        this.ui.registerSuccess = true
        sendRegistrationSuccessMessage(this.local.event.id)
        this.$nextTick(() => {
          setTimeout(() => {
            sendPostMessage()
          }, 300)
        })
      } catch(err) {
        console.error(err.response)
        this.ui.registerError = true
        this.handleError(err.response)
      }
      console.log(sendResult)
      this.ui.sendData = false
    },
    handleError(err) {
      console.warn(err);
      if (err && err.data) {
        this.local.registerErrorMsg = this.t('registration_error_message_prefix', 'Fehlermeldung:') + ' ' + err.data.message + ". "
        const errors = Array.isArray(err.data.errors) ? err.data.errors : []
        for (let e of errors) {
          if (e.message === "uniqueParticipantEmailPerEvent must be unique") {
            this.local.registerErrorMsg = this.t('registration_duplicate_email_error', 'Sie haben sich bereits mit dieser E-Mail-Adresse angemeldet, bitte schauen Sie auch im Spam-Ordner nach.')
          }else {
            this.local.registerErrorMsg = this.local.registerErrorMsg + e.message + ". "
          }
        }
      } else {
        this.local.registerErrorMsg = this.t(
          'registration_closed_text',
          'Eine Anmeldung für dieses Event ist nicht mehr möglich.'
        )
      }
    }
  },
  updated() {
    this.$nextTick(sendPostMessage)
  },
  beforeDestroy () {
    if (this.availabilityTimer) {
      clearInterval(this.availabilityTimer)
    }
  }
}
</script>
